import { useState, useEffect } from 'react';
import StatCard from "../../../components/common/StatCard";
import ComponentCard from "../../../components/common/ComponentCard";
import Button from "../../../components/ui/button/Button";
import { DollarSign, FileText, Plus, Download, TrendingUp, AlertCircle, RefreshCw, CreditCard, Edit3, Check, Info, Calendar } from 'lucide-react';
import useApi from "../../../hooks/useApi";
import NotasList from "./NotasList";
import { Modal } from "../../../components/ui/modal";
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import Select from "../../../components/form/Select";
import Input from "../../../components/form/input/InputField";
import CreateNotaModal from "./CreateNotaModal";
import { Nota, ExportNotasFilters } from '../types/index';
import type { NotaStats } from '../types/index';
import useAuth from "../../../hooks/useAuth";
import RoleBasedRenderer from "../../../components/auth/RoleBasedRenderer";

export default function NotasView() {
  const { 
    formatCurrency,
    getNotas,
    getNotaStats,
    exportNotas,
    getFacturas
  } = useApi();
  
  const { canCreate } = useAuth();
  
  // State management
  const [notas, setNotas] = useState<Nota[]>([]);
  const [stats, setStats] = useState<NotaStats>({
    totalNotas: 0,
    notasDelMes: 0,
    notasDelMesAnterior: 0,
    porTipo: {
      credito: 0,
      debito: 0
    },
    porMotivo: {
      error: 0,
      descuento: 0,
      ajuste: 0,
      otro: 0
    },
    tendencias: {
      notas: 0
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState<ExportNotasFilters>({
    startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    tipo: '',
    facturaNumero: '',
    motivo: ''
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{id: number, numero: string, clienteNombre: string}>>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<{id: number, numero: string, clienteNombre: string} | null>(null);
  
  // Function to refresh stats
  const refreshStats = async () => {
    try {
      const statsData = await getNotaStats();
      if (statsData) {
        setStats({
          ...statsData,
          notasDelMesAnterior: (statsData as any).notasDelMesAnterior || 0,
          tendencias: statsData.tendencias || { notas: 0 }
        });
      }
    } catch (error) {
      toast.error(
        'No se pudieron cargar las estadísticas. Por favor, actualice la página.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    }
  };
  
  // Function to refresh all data
  const refreshData = async () => {
    try {
      setIsLoading(true);
      
      await Promise.all([
        refreshStats(),
        getNotas().then(notasData => {
          setNotas(notasData.map(nota => ({
            ...nota,
            impactoSaldo: nota.impactoSaldo ?? 0
          })));
        })
      ]);
      
    } catch (error) {
      toast.error(
        'Error al cargar los datos. Por favor, verifique su conexión e inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    refreshData();
  }, []);
  
  // Handle exporting data with filters
  const handleExportData = async () => {
    try {
      await exportNotas(exportFilters);
      setIsExportModalOpen(false);
      toast.success(
        'Datos exportados correctamente a Excel',
        {
          duration: 4000,
          icon: <Check className="w-4 h-4 text-green-500" />,
        }
      );
    } catch (error) {
      toast.error(
        'No se pudieron exportar los datos. Verifique los filtros e inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    }
  };
  
  // Add handleSearch function
  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    
    // Update the facturaNumero field in exportFilters for free text search
    setExportFilters(prev => ({
      ...prev,
      facturaNumero: value,
      facturaId: undefined
    }));
    
    if (value.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }
    
    setLoadingSearch(true);
    setShowSuggestions(true);
    
    try {
      // Fetch facturas that match the search term
      const facturas = await getFacturas();
      
      const filteredFacturas = facturas
        .filter(factura => 
          factura.numero.toLowerCase().includes(value.toLowerCase()) ||
          (factura.clienteNombre && factura.clienteNombre.toLowerCase().includes(value.toLowerCase()))
        )
        .filter(factura => factura.id !== undefined) // Ensure id exists
        .map(factura => ({
          id: factura.id!,
          numero: factura.numero,
          clienteNombre: factura.clienteNombre || 'Cliente sin nombre'
        }));
      
      setSearchResults(filteredFacturas);
    } catch (error) {
      console.error('Error searching facturas', error);
      toast.error(
        'Error al buscar facturas',
        {
          duration: 3000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setLoadingSearch(false);
    }
  };
  
  const handleSelectFactura = (factura: {id: number, numero: string, clienteNombre: string}) => {
    setSelectedFactura(factura);
    setSearchTerm(factura.numero);
    setExportFilters(prev => ({
      ...prev,
      facturaNumero: factura.numero,
      facturaId: factura.id
    }));
    setShowSuggestions(false);
  };

  const handleOpenExportModal = () => {
    setExportFilters({
      startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      tipo: '',
      facturaNumero: '',
      motivo: ''
    });
    setSearchTerm('');
    setSearchResults([]);
    setSelectedFactura(null);
    setIsExportModalOpen(true);
  };
  
  // Handle filter changes in export modal
  const handleFilterChange = (value: string, name: string) => {
    setExportFilters((prev: ExportNotasFilters) => ({ ...prev, [name]: value }));
  };
  
  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard 
              title="Total notas" 
              value={isLoading ? '---' : stats.totalNotas.toString()} 
              icon={<FileText className="w-6 h-6" />}
              trend={0}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard 
              title="Notas del mes" 
              value={isLoading ? '---' : stats.notasDelMes.toString()} 
              icon={<Calendar className="w-6 h-6" />}
              trend={0}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard 
              title="Notas de crédito" 
              value={isLoading ? '---' : stats.porTipo.credito.toString()} 
              icon={<CreditCard className="w-6 h-6" />}
              trend={0}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard 
              title="Tendencia mensual" 
              value={isLoading ? '---' : stats.tendencias.notas > 0 ? `+${stats.tendencias.notas}%` : `${stats.tendencias.notas}%`} 
              icon={<TrendingUp className="w-6 h-6" />}
              trend={!isLoading ? stats.tendencias.notas : 0}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <NotasList 
            notas={notas} 
            onDataChange={refreshData}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ComponentCard title="Acciones rápidas">
            <div className="space-y-3 p-4">
              <RoleBasedRenderer requiredPermission="create">
                <Button 
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  startIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setIsCreateModalOpen(true)} 
                >
                  Nueva nota
                </Button>
              </RoleBasedRenderer>
              <Button 
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<Download className="w-4 h-4" />}
                onClick={handleOpenExportModal} 
              >
                Exportar datos
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full justify-start"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={refreshData}
              >
                Actualizar datos
              </Button>
            </div>
          </ComponentCard>

          <ComponentCard title="Resumen por tipo">
            <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porTipo.credito}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">crédito</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porTipo.debito}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">débito</span>
                  </div>
                </div>
              </div>
            </div>
          </ComponentCard>
          
          <ComponentCard title="Resumen por motivo">
            <div className="space-y-3 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                <div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isLoading ? '---' : stats.porMotivo.ajuste}
                  </span>
                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">ajustes</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isLoading ? '---' : stats.porMotivo.error}
                  </span>
                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">errores</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isLoading ? '---' : stats.porMotivo.descuento}
                  </span>
                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">descuentos</span>
                </div>
              </div>
            </div>
          </ComponentCard>
        </div>
      </div>

      {/* Modal para exportar */}
      {isExportModalOpen && (
        <Modal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          title="Exportar notas a Excel"
        >
          <div className="space-y-4 pb-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Seleccione el período y los filtros para las notas a exportar
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha inicio
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={exportFilters.startDate}
                  onChange={(e) => handleFilterChange(e.target.value, 'startDate')}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={exportFilters.endDate}
                  onChange={(e) => handleFilterChange(e.target.value, 'endDate')}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de nota
                </label>
                <select
                  id="tipo"
                  value={exportFilters.tipo}
                  onChange={(e) => handleFilterChange(e.target.value, 'tipo')}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  <option value="credito">Crédito</option>
                  <option value="debito">Débito</option>
                </select>
              </div>
              <div>
                <label htmlFor="motivo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Motivo
                </label>
                <select
                  id="motivo"
                  value={exportFilters.motivo}
                  onChange={(e) => handleFilterChange(e.target.value, 'motivo')}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  <option value="error">Error</option>
                  <option value="descuento">Descuento</option>
                  <option value="ajuste">Ajuste</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="facturaSearch" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Factura
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="facturaSearch"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Buscar por número de factura o cliente"
                />
                
                {loadingSearch && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
                  </div>
                )}
                
                {showSuggestions && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {searchResults.map((result) => (
                        <li 
                          key={result.id}
                          className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between text-gray-800 dark:text-gray-200"
                          onClick={() => handleSelectFactura(result)}
                        >
                          <div className="flex items-center">
                            <span className="font-medium">{result.numero}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {result.clienteNombre}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {showSuggestions && searchTerm.length >= 2 && searchResults.length === 0 && !loadingSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-2 px-4 text-sm text-gray-500 dark:text-gray-400">
                    No se encontraron coincidencias. Se utilizará el texto ingresado.
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Busque por número de factura o nombre del cliente
              </p>
            </div>
            
            <div className="pt-4 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsExportModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleExportData}
              >
                Exportar
              </Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* CreateNotaModal component */}
      <CreateNotaModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={refreshData}
      />
    </div>
  );
} 